import { yupResolver } from '@hookform/resolvers/yup';
import { Grid, Stack } from '@mui/material';
import { useState } from 'react';
import { useForm, FormProvider, FieldValues } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { loginForgotPassword, amplitude } from 'wisestep-api';
import {
  WsTypoColors,
  Item,
  WsTypography,
  WsTypoVariants,
  ControlledTextInput,
  WsButton,
  Notification,
  WisestepLogo,
  Label,
} from 'wisestep-base';
import { object, string } from 'yup';

import { AuthLayout, WsLink } from '../../../components';

interface IForgotPasswordForm {
  email: string;
}

const validationSchema = object().shape({
  email: string().required('Name is required'),
});

const ForgotPassword = () => {
  const [notify, setNotify] = useState<boolean>(false);

  const [notificationObject, setNotificationObject] = useState<{
    message: string;
    type: 'error' | 'info' | 'success' | 'warning';
  }>();

  const formOptions = {
    resolver: yupResolver(validationSchema),
    defaultValues: {
      email: '',
    },
  };

  const methods = useForm<IForgotPasswordForm>(formOptions);
  const navigate = useNavigate();

  const { handleSubmit } = methods;

  const handleForgotPassword = (data: FieldValues) => {
    const email = data.email;
    loginForgotPassword(email)
      .then(() =>
        navigate('/reset-initiated', {
          state: {
            email: email,
          },
        })
      )
      .catch((error) => {
        setNotify(true);
        if (
          error.response.status === 400 ||
          error.response.statusText === 'email address not registered'
        ) {
          setNotificationObject({
            message: 'Email not registered',
            type: 'error',
          });
        } else {
          setNotificationObject({
            message: 'Some error occurred. Please try later.',
            type: 'error',
          });
        }
      });
  };

  return (
    <AuthLayout>
      <Item>
        <FormProvider {...methods}>
          <form onSubmit={handleSubmit(handleForgotPassword)}>
            <Grid item xs={12}>
              <Item>
                <Stack alignItems="center">
                  <WisestepLogo />
                </Stack>
                <Stack mt={10} alignItems="center">
                  <Label sx={{ fontSize: '28px', fontWeight: 'bold' }}>Reset Your Password</Label>
                </Stack>
                <Stack mt={3}>
                  <WsTypography variant={WsTypoVariants.TITLE}>
                    Please enter the email address you used
                  </WsTypography>
                  <WsTypography variant={WsTypoVariants.TITLE}>
                    to create your WiseStep account, and we
                  </WsTypography>
                  <WsTypography variant={WsTypoVariants.TITLE}>
                    will send you a link to reset your password.
                  </WsTypography>
                </Stack>
                <Stack mt={2} direction="column" spacing={2}>
                  <ControlledTextInput
                    name={'email'}
                    labelText="Email Address *"
                    fullWidth
                    id="password"
                    placeholder="eg. johndoe@email.com"
                    variant="outlined"
                    required
                    autoComplete="forgotpassword"
                  />
                </Stack>
                <Stack mt={2}>
                  <WsButton
                    variant="contained"
                    size="large"
                    fullWidth
                    type="submit"
                    onClick={() =>
                      amplitude('Forgot_Reset_Password', {
                        clickedOn: 'Reset Password',
                        pageName: 'Login',
                        pageUrl: window.location.href,
                        interfaceType: 'Web',
                      })
                    }
                  >
                    Reset
                  </WsButton>
                </Stack>
                <Notification
                  vertical="bottom"
                  horizontal="center"
                  autoHideDuration={6000}
                  notify={notify}
                  notificationObject={notificationObject}
                />
              </Item>
              <Stack justifyContent="center" alignItems="center" direction="row" spacing={1}>
                <WsLink to="/">
                  <WsTypography
                    variant={WsTypoVariants.BODY1}
                    color={WsTypoColors.PRIMARY}
                    cursor="pointer"
                  >
                    Log in
                  </WsTypography>
                </WsLink>
                <WsTypography variant={WsTypoVariants.BODY1}>/</WsTypography>
                <WsLink to="/sign-up">
                  <WsTypography
                    variant={WsTypoVariants.BODY1}
                    color={WsTypoColors.PRIMARY}
                    cursor="pointer"
                  >
                    Sign Up
                  </WsTypography>
                </WsLink>
              </Stack>
            </Grid>
          </form>
        </FormProvider>
      </Item>
    </AuthLayout>
  );
};

export default ForgotPassword;
